/**
 * `SettingsInstall` — the Install tab.
 *
 * One coherent page, sections in reading order:
 *
 *   1. Install coverage — "What Sill sees on {domain}", the measured
 *      install coverage (browser script, server-side layer, discovery
 *      files). The one card the merchant checks first to know "is
 *      this live?". The InstallCoverageCard carries its own kicker;
 *      the wrapper intentionally has no title or subtitle so the three
 *      stacked headings ("How Sill is installed" / "What reaches Sill
 *      ..." / "WHAT SILL SEES ON ...") collapse to one.
 *   2. Cloudflare Worker + plugin download — the install actions,
 *      bare (no "Agent Readiness" leftover title; no coverage card
 *      duplicated inside — that is section 1).
 *   3. Script snippet + key rotation + addresses agents use + protocol
 *      list — the merchant-facing agent surface. Rendered through
 *      `SettingsEndpoint` with the rescan picker hidden (that control
 *      lives on the Agent Readiness page header).
 */

import { AlertTriangle, ArrowDown } from 'lucide-react';

import type { Site, SiteInstallUpdate } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { useAuth } from '../../app/auth-context.js';
import { useC } from '../../lib/theme.js';
import { Card } from '../../components/atoms/index.js';
import { installUpdateCopy } from '../../lib/install-update-copy.js';
import { InstallCoverageCard } from '../sites/InstallCoverageCard.js';
import { SettingsEndpoint } from './SettingsEndpoint.js';
import { AgentReadinessCard } from './SettingsIntegrations.js';

export interface SettingsInstallProps {
  readonly site: Site;
}

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export function SettingsInstall({ site }: SettingsInstallProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const auth = useAuth();
  const role = auth.state.kind === 'authenticated' ? auth.state.user.role : null;
  const canManage = role === 'owner' || role === 'admin';

  return (
    <div className="flex flex-col gap-6">
      {/* Founder 2026-10-27 — when the install is behind, say exactly
          which install and what to do, first thing on the tab. */}
      {site.install_update !== undefined ? (
        <div id="install-update" style={{ scrollMarginTop: 96 }}>
          <InstallUpdateCallout C={C} update={site.install_update} />
        </div>
      ) : null}

      {/* Section 1 — Install coverage ("What Sill sees on {domain}").
          The InstallCoverageCard supplies the only heading here; no
          SectionHeader so the three stacked titles collapse to one
          (founder 2026-10-28). Stable id for deep links. */}
      <div id="how-installed">
      <Card>
        <div style={{ padding: '20px 24px 24px' }}>
          <InstallCoverageCard site={site} bare />
        </div>
      </Card>
      </div>

      {/* Section 2 — Deploy / redeploy / download install artifacts. */}
      <div id="install">
      <Card>
        <SectionHeader
          C={C}
          title="Install Sill on your site"
          subtitle="Deploy the Cloudflare Worker in one click, or download the Worker / WordPress plugin to install yourself."
        />
        <div style={{ padding: '20px 24px 24px' }}>
          <AgentReadinessCard
            C={C}
            siteId={site.site_id}
            site={site}
            api={api.sites}
            canManage={canManage}
            bare
            hideCoverageCard
          />
        </div>
      </Card>
      </div>

      {/* Section 3 — Script snippet + key rotation + addresses +
          protocol support. Owned by `SettingsEndpoint`. */}
      <div id="endpoint">
        <SettingsEndpoint site={site} hideRescan />
      </div>
    </div>
  );
}

function SectionHeader({
  C, title, subtitle,
}: {
  readonly C: ReturnType<typeof useC>;
  readonly title: string;
  readonly subtitle: string;
}): JSX.Element {
  return (
    <div
      style={{
        padding: '20px 24px 16px',
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <h2
        style={{
          margin: 0,
          fontFamily: serifFont,
          fontSize: 22,
          color: C.text,
          letterSpacing: -0.5,
          fontWeight: 400,
        }}
      >
        {title}
      </h2>
      <div
        style={{
          marginTop: 4,
          fontFamily: sansFont,
          fontSize: 13,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        {subtitle}
      </div>
    </div>
  );
}

function InstallUpdateCallout({
  C, update,
}: {
  readonly C: ReturnType<typeof useC>;
  readonly update: SiteInstallUpdate;
}): JSX.Element {
  const copy = installUpdateCopy(update);
  const target = copy.action === 'upload' ? 'install' : copy.action === 'redeploy' ? 'cloudflare' : null;
  return (
    <Card>
      <div
        data-testid="install-update-callout"
        data-action={copy.action}
        style={{ padding: '20px 24px 22px', borderLeft: `3px solid ${C.amberFg}` }}
      >
        <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
          <AlertTriangle size={12} color={C.amberFg} />
          <span style={{ fontFamily: monoFont, fontSize: 10, color: C.amberFg, letterSpacing: 1, fontWeight: 600 }}>
            UPDATE AVAILABLE
          </span>
        </div>
        <h2 style={{ margin: 0, fontFamily: serifFont, fontSize: 22, color: C.text, letterSpacing: -0.5, fontWeight: 400 }}>
          {copy.install} <span style={{ fontFamily: monoFont, fontSize: 14, color: C.muted, letterSpacing: 0 }}>{copy.versions}</span>
        </h2>
        <ol style={{ margin: '12px 0 0', paddingLeft: 20, listStyle: 'decimal', fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.6 }}>
          {copy.steps.map((step) => <li key={step}>{step}</li>)}
        </ol>
        {target !== null ? (
          <button
            type="button"
            onClick={() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="inline-flex items-center gap-2 px-4 py-2"
            style={{ marginTop: 14, background: C.lime, color: C.ink, border: 'none', fontFamily: sansFont, fontSize: 13, fontWeight: 600, borderRadius: 2, cursor: 'pointer' }}
            data-testid="install-update-jump"
          >
            {copy.action === 'upload' ? 'Go to the download' : 'Go to the Cloudflare card'} <ArrowDown size={13} />
          </button>
        ) : null}
      </div>
    </Card>
  );
}
